import type { AssessmentDelivery, AssessmentResult, AssessmentScope } from "@peer-start/platform-contracts";
import { Button, PageHeading } from "@peer-start/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BrainCircuit } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useReturnNavigation } from "../../../app/navigation/return-navigation.js";
import { BackLink } from "../../../app/components/BackLink.js";
import { useAppContext } from "../../../app/use-app-context.js";
import { PassportQueryState } from "../../passport/components/PassportQueryState.js";
import { fetchAssessmentExample, fetchAssessmentResult } from "../api/client.js";
import { useAssessment, useLatestAssessment } from "../api/queries.js";
import { AssessmentIntro } from "../components/AssessmentIntro.js";
import { AssessmentQuestion } from "../components/AssessmentQuestion.js";
import { AssessmentResultView } from "../components/AssessmentResultView.js";

export function KnowledgeAssessmentPage({
  id,
  pageId,
  scope = "topic",
}: {
  id: string;
  pageId: string;
  scope?: AssessmentScope;
}) {
  const { t } = useTranslation();
  const { persona } = useAppContext();
  const query = useAssessment(id);
  const latest = useLatestAssessment(id, scope === "route" ? "route" : undefined);
  const back = useReturnNavigation(scope === "route" ? "/me" : `/me/passport/${pageId}`);
  const cache = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const [running, setRunning] = useState<AssessmentDelivery | null>(null);
  const resultId = search.get("result");
  const result = useQuery({
    queryKey: ["assessment-result", persona.id, id, resultId],
    queryFn: () => (resultId === "example" ? fetchAssessmentExample(id) : fetchAssessmentResult(resultId!)),
    enabled: !!resultId,
  });
  const showResult = (attemptId: string) => {
    setRunning(null);
    setSearch((previous) => {
      const next = new URLSearchParams(previous);
      next.set("assessment", id);
      next.set("result", attemptId);
      return next;
    });
  };
  const completed = (value: AssessmentResult) => {
    cache.setQueryData(["assessment-result", persona.id, id, value.attemptId], value);
    void cache.invalidateQueries({ queryKey: ["assessment-latest", persona.id, id] });
    void cache.invalidateQueries({ queryKey: ["assessment-overview", persona.id] });
    void cache.invalidateQueries({ queryKey: ["passport-bootstrap"] });
    void cache.invalidateQueries({ queryKey: ["passport-page"] });
    void cache.invalidateQueries({ queryKey: ["passport-stamp"] });
    showResult(value.attemptId);
  };
  const intro = () => {
    setRunning(null);
    setSearch((previous) => {
      const next = new URLSearchParams(previous);
      next.set("assessment", id);
      next.delete("result");
      return next;
    });
  };
  return (
    <div className="mx-auto max-w-5xl">
      <BackLink controlId="assessment.back" to={back.to}>
        {t(back.labelKey)}
      </BackLink>
      <PassportQueryState
        error={query.isError}
        loading={query.isLoading}
        onRetry={() => void query.refetch()}
      >
        {query.data ? (
          <>
            <PageHeading
              title={scope === "route" ? t("knowledgeAssessment.routeTitle") : query.data.definition.title}
              subtitle={t(
                scope === "route" ? "knowledgeAssessment.routeSubtitle" : "knowledgeAssessment.topicSubtitle",
              )}
              eyebrow={
                <>
                  <BrainCircuit aria-hidden="true" className="size-5" />
                  {t("knowledgeAssessment.eyebrow")}
                </>
              }
            />
            <div className="mb-5 flex gap-2" aria-label={t("knowledgeAssessment.views")}>
              <Button
                controlId="assessment.view.test"
                variant={!resultId ? "primary" : "secondary"}
                onClick={intro}
              >
                {t("knowledgeAssessment.testTab")}
              </Button>
              <Button
                controlId="assessment.view.result"
                variant={resultId ? "primary" : "secondary"}
                disabled={!latest.data && !resultId}
                onClick={() => showResult(latest.data?.attemptId ?? resultId!)}
              >
                {t("knowledgeAssessment.resultTab")}
              </Button>
            </div>
            <p className="mb-5 rounded-xl border border-warning-foreground/20 bg-warning p-3 text-sm text-warning-foreground">
              {t("knowledgeAssessment.validationNote")}
            </p>
            {resultId ? (
              <PassportQueryState
                error={result.isError}
                loading={result.isLoading}
                onRetry={() => void result.refetch()}
              >
                {result.data ? <AssessmentResultView result={result.data} /> : null}
              </PassportQueryState>
            ) : running ? (
              <AssessmentQuestion delivery={running} scope={scope} onComplete={completed} />
            ) : (
              <AssessmentIntro
                delivery={query.data}
                scope={scope}
                hasResult={!!latest.data}
                onStart={() => setRunning(query.data!)}
                onExample={() => showResult("example")}
                onResult={() => showResult(latest.data!.attemptId)}
              />
            )}
          </>
        ) : null}
      </PassportQueryState>
    </div>
  );
}
